/**
 * 浏览撮合域 API(FR-4)。类型引用生成的 `components['schemas']`,走 `call<T>()` 解 envelope。
 * 端点(全 GET,游客可访问,同源相对路径由反代转后端):
 *   GET /api/v1/guide/home-feed           cursor 分页(size 20)   → ProductFeedResponse
 *   GET /api/v1/search/products           page 分页(size 20)     → ProductSearchResponse
 *   GET /api/v1/guide/categories          扁平品类树             → CategoryNodeDTO[]
 *   GET /api/v1/guide/product-detail/{id}                        → ProductDetailResponse
 *   GET /api/v1/products/{id}/reviews      page 分页              → ReviewListResponse
 * 社交动作(需 JWT,详情页操作区乐观切换,消费现有端点、零后端改动):
 *   POST/DELETE /api/v1/social/{likes,follows,favorites}
 */
import { call } from './client';
import type { components } from './openapi.generated';

type S = components['schemas'];
export type ProductFeedItem = S['ProductFeedItemDTO'];
export type ProductFeedResponse = S['ProductFeedResponse'];
export type ProductSearchResponse = S['ProductSearchResponse'];
export type ProductDetail = S['ProductDetailResponse'];
export type CategoryNode = S['CategoryNodeDTO'];
export type ReviewListResponse = S['ReviewListResponse'];
export type ReviewItem = S['ReviewItem'];

const API = '/api/v1';
const PAGE_SIZE = 20;

export interface FeedParams {
  cursor?: string;
  size?: number;
  categoryL1Id?: string;
}
/** 首页 / 品类专题推荐流(cursor 分页)。 */
export function getHomeFeed(params: FeedParams): Promise<ProductFeedResponse> {
  return call<ProductFeedResponse>({
    method: 'GET',
    url: `${API}/guide/home-feed`,
    params: { size: PAGE_SIZE, ...params },
  });
}

export interface SearchParams {
  q: string;
  page?: number;
  size?: number;
  categoryL1Id?: string;
}
/** 关键词搜索(page 分页)。 */
export function searchProducts(params: SearchParams): Promise<ProductSearchResponse> {
  return call<ProductSearchResponse>({
    method: 'GET',
    url: `${API}/search/products`,
    params: { size: PAGE_SIZE, ...params },
  });
}

/** 扁平品类树(L1/L2 由前端 categoryTree 派生)。 */
export function getCategories(): Promise<CategoryNode[]> {
  return call<CategoryNode[]>({ method: 'GET', url: `${API}/guide/categories` });
}

/** 商品详情。 */
export function getProductDetail(id: string): Promise<ProductDetail> {
  return call<ProductDetail>({
    method: 'GET',
    url: `${API}/guide/product-detail/${encodeURIComponent(id)}`,
  });
}

/** 商品评价列表(首屏取前几条作社会证明;摘要在 detail.reviewSummary)。 */
export function getProductReviews(id: string, page = 1, size = 3): Promise<ReviewListResponse> {
  return call<ReviewListResponse>({
    method: 'GET',
    url: `${API}/products/${encodeURIComponent(id)}/reviews`,
    params: { page, size },
  });
}

// ---- 社交动作(乐观切换;幂等,失败回滚)----
export function addFavorite(productId: string): Promise<unknown> {
  return call({
    method: 'POST',
    url: `${API}/social/favorites`,
    data: { productId } satisfies S['AddFavoriteRequest'],
  });
}
export function removeFavorite(productId: string): Promise<unknown> {
  return call({ method: 'DELETE', url: `${API}/social/favorites/${encodeURIComponent(productId)}` });
}
export function likeProduct(productId: string): Promise<unknown> {
  return call({
    method: 'POST',
    url: `${API}/social/likes`,
    data: { productId } satisfies S['LikeProductRequest'],
  });
}
export function unlikeProduct(productId: string): Promise<unknown> {
  return call({ method: 'DELETE', url: `${API}/social/likes/${encodeURIComponent(productId)}` });
}
export function followSeller(sellerId: string): Promise<unknown> {
  return call({
    method: 'POST',
    url: `${API}/social/follows`,
    data: { sellerId } satisfies S['FollowSellerRequest'],
  });
}
export function unfollowSeller(sellerId: string): Promise<unknown> {
  return call({ method: 'DELETE', url: `${API}/social/follows/${encodeURIComponent(sellerId)}` });
}
